import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box, Typography, Paper, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Grid,
  Table, TableBody, TableCell, TableHead, TableRow, Chip,
  IconButton, Alert, Snackbar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyIcon from '@mui/icons-material/Key';
import { UserRole } from '../types/user';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { Id } from '../convex/_generated/dataModel';
import { useAuth } from '../contexts/AuthContext';
import { useAuthActions } from '@convex-dev/auth/react';

interface ConvexUser {
  _id: Id<'users'>;
  userId: Id<'users'>;
  email: string;
  name: string;
  role: UserRole;
  url: string;
  phone?: string;
  immunocalUrl?: string;
  consultorId?: string;
  createdAt: number;
  createdByName?: string;
  createdByEmail?: string;
}

export const UsersPage = (): React.ReactElement => {
  const { userData } = useAuth();
  const { signIn } = useAuthActions();

  const rawUsers = useQuery(api.users.listUsers) ?? [];
  const users = rawUsers as ConvexUser[];

  const updateUser = useMutation(api.users.updateUser);
  const deleteUser = useMutation(api.users.deleteUser);
  const changePassword = useMutation(api.users.changeUserPassword);
  const registerProfile = useMutation(api.users.registerUserProfile);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedQrUser, setSelectedQrUser] = useState<ConvexUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<ConvexUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ConvexUser | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [formData, setFormData] = useState({
    email: '', password: '', name: '', phone: '', url: '',
    role: 'colaborador' as UserRole, immunocalUrl: '', consultorId: '',
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    let result = [...users];
    if (nameFilter) result = result.filter(u => u.name.toLowerCase().includes(nameFilter.toLowerCase()));
    if (emailFilter) result = result.filter(u => u.email.toLowerCase().includes(emailFilter.toLowerCase()));
    if (roleFilter) result = result.filter(u => u.role === roleFilter);
    setFilteredUsers(result);
  }, [users, nameFilter, emailFilter, roleFilter]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateClose = () => {
    setCreateDialogOpen(false);
    setError('');
    setFormData({ email: '', password: '', name: '', phone: '', url: '', role: 'colaborador', immunocalUrl: '', consultorId: '' });
  };

  const handleEditOpen = (user: ConvexUser) => {
    setSelectedUser(user);
    setFormData({
      email: user.email, password: '', name: user.name, phone: user.phone || '',
      url: user.url, role: user.role, immunocalUrl: user.immunocalUrl || '',
      consultorId: user.consultorId || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => { setEditDialogOpen(false); setSelectedUser(null); setError(''); };
  const handleQrOpen = (user: ConvexUser) => { setSelectedQrUser(user); setQrDialogOpen(true); };
  const handleQrClose = () => { setQrDialogOpen(false); setSelectedQrUser(null); };
  const handleDeleteOpen = (user: ConvexUser) => { setSelectedUser(user); setDeleteDialogOpen(true); };
  const handleDeleteClose = () => { setDeleteDialogOpen(false); setSelectedUser(null); setError(''); };
  const handlePasswordOpen = (user: ConvexUser) => { setSelectedUser(user); setNewPassword(''); setPasswordDialogOpen(true); };
  const handlePasswordClose = () => { setPasswordDialogOpen(false); setSelectedUser(null); setError(''); setNewPassword(''); };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.email || !formData.password || !formData.name || !formData.url) {
        setError('Todos los campos requeridos deben estar completos');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setError('El formato del email no es válido');
        return;
      }
      if ((formData.role === 'consultor' || formData.role === 'admin') && !formData.immunocalUrl) {
        setError('La URL de la tienda Immunocal es requerida para consultores y administradores');
        return;
      }
      if (formData.immunocalUrl && !formData.immunocalUrl.startsWith('http')) {
        setError('La URL de la tienda debe comenzar con http:// o https://');
        return;
      }

      // Verificar URL única
      const urlExists = users.some(u => u.url === formData.url);
      if (urlExists) {
        setError('La URL personalizada ya está en uso');
        return;
      }

      const effectiveConsultorId = formData.role === 'colaborador'
        ? (userData?.role === 'consultor' ? userData.uid : formData.consultorId)
        : undefined;

      if (formData.role === 'colaborador' && !effectiveConsultorId) {
        setError('Un colaborador debe tener un consultor asignado');
        return;
      }

      // Crear cuenta en Convex Auth usando signUp flow
      // Guardamos la sesión actual para restaurarla
      await signIn('password', {
        email: formData.email,
        password: formData.password,
        flow: 'signUp',
        name: formData.name,
        url: formData.url,
        role: formData.role,
        phone: formData.phone,
        immunocalUrl: formData.immunocalUrl,
        consultorId: effectiveConsultorId,
        createdByUid: userData?.uid || '',
        createdByName: userData?.name || '',
        createdByEmail: userData?.email || '',
      } as any);

      // Nota: signUp con Convex Auth crea la sesión del nuevo usuario.
      // Necesitamos registrar el perfil y luego restaurar la sesión del admin.
      // Por simplicidad, el perfil se registra mediante el hook afterSignUp en el backend.
      // Aquí lo hacemos directamente con la mutation registerUserProfile.
      await registerProfile({
        email: formData.email,
        name: formData.name,
        phone: formData.phone,
        url: formData.url,
        role: formData.role,
        immunocalUrl: formData.immunocalUrl || undefined,
        consultorId: effectiveConsultorId,
        createdByUid: userData?.uid,
        createdByName: userData?.name,
        createdByEmail: userData?.email,
      });

      setSnackbarMessage('Usuario creado exitosamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCreateClose();
    } catch (err) {
      console.error('Error al crear usuario:', err);
      setError(err instanceof Error ? `Error: ${err.message}` : 'Error al crear el usuario.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    setLoading(true);
    setError('');

    try {
      if (!formData.name || !formData.url) {
        setError('Todos los campos requeridos deben estar completos');
        return;
      }
      // Validar URL única (excepto la misma URL del usuario)
      const urlExists = users.some(u => u.url === formData.url && u._id !== selectedUser._id);
      if (urlExists) {
        setError('La URL personalizada ya está en uso');
        return;
      }
      if ((formData.role === 'consultor' || formData.role === 'admin') && !formData.immunocalUrl) {
        setError('La URL de la tienda Immunocal es requerida para consultores y administradores');
        return;
      }
      if (formData.role === 'colaborador' && !formData.consultorId) {
        setError('Un colaborador debe tener un consultor asignado');
        return;
      }

      await updateUser({
        id: selectedUser._id,
        name: formData.name,
        phone: formData.phone,
        url: formData.url,
        role: formData.role,
        immunocalUrl: formData.immunocalUrl || undefined,
        consultorId: formData.role === 'colaborador'
          ? (userData?.role === 'consultor' ? userData.uid : formData.consultorId)
          : (formData.role === 'admin' ? undefined : undefined),
      });

      setSnackbarMessage('Usuario actualizado exitosamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleEditClose();
    } catch (err) {
      console.error('Error al actualizar usuario:', err);
      setError(err instanceof Error ? `Error: ${err.message}` : 'Error al actualizar el usuario.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setError('');
    try {
      await deleteUser({ id: selectedUser._id });
      setSnackbarMessage('Usuario eliminado exitosamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleDeleteClose();
    } catch (err) {
      console.error('Error al eliminar usuario:', err);
      setError(err instanceof Error ? `Error: ${err.message}` : 'Error al eliminar el usuario.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setError('');
    try {
      if (!newPassword || newPassword.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return;
      }
      await changePassword({ id: selectedUser._id, newPassword });
      setSnackbarMessage('Contraseña actualizada exitosamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handlePasswordClose();
    } catch (err) {
      console.error('Error al cambiar contraseña:', err);
      setError(err instanceof Error ? `Error: ${err.message}` : 'Error al cambiar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gestión de Usuarios</Typography>
        {(userData?.role === 'admin' || userData?.role === 'consultor') && (
          <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>Crear Usuario</Button>
        )}
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Filtros</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Filtrar por nombre" value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Filtrar por email" value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField select fullWidth label="Filtrar por rol" value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)} size="small">
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="consultor">Consultor</MenuItem>
              <MenuItem value="colaborador">Colaborador</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="outlined" onClick={() => { setNameFilter(''); setEmailFilter(''); setRoleFilter(''); }} fullWidth>
              Limpiar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla */}
      <Paper sx={{ mt: 3, overflow: 'hidden' }}>
        {filteredUsers.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><Typography>No hay usuarios para mostrar</Typography></Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Creado por</TableCell>
                <TableCell>Fecha creación</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map(user => (
                <TableRow key={user._id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip label={user.role}
                      color={user.role === 'admin' ? 'error' : user.role === 'consultor' ? 'primary' : 'default'}
                      size="small" />
                  </TableCell>
                  <TableCell>{user.url}</TableCell>
                  <TableCell>{user.createdByName || 'No disponible'}</TableCell>
                  <TableCell>
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleString('es-ES', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })
                      : 'No disponible'}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditOpen(user)} title="Editar usuario">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleQrOpen(user)} title="Ver código QR">
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                    {userData?.role === 'admin' && (
                      <>
                        <IconButton size="small" onClick={() => handlePasswordOpen(user)} title="Cambiar contraseña" color="primary">
                          <KeyIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteOpen(user)} title="Eliminar usuario" color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Diálogo Crear */}
      <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
        {error && <Box sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>{error}</Box>}
        <DialogTitle>Crear Nuevo Usuario</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleCreateSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField required fullWidth name="email" label="Email" value={formData.email} onChange={handleChange} /></Grid>
              <Grid item xs={12}><TextField required fullWidth name="password" label="Contraseña" type="password" value={formData.password} onChange={handleChange} /></Grid>
              <Grid item xs={12}><TextField required fullWidth name="name" label="Nombre" value={formData.name} onChange={handleChange} /></Grid>
              <Grid item xs={12}><TextField fullWidth name="phone" label="Teléfono" value={formData.phone} onChange={handleChange} /></Grid>
              <Grid item xs={12}><TextField required fullWidth name="url" label="URL Personalizada" value={formData.url} onChange={handleChange} helperText="Esta URL será parte de su enlace público" /></Grid>
              <Grid item xs={12}>
                <TextField required select fullWidth name="role" label="Rol" value={formData.role} onChange={handleChange}
                  disabled={userData?.role !== 'admin' && userData?.role !== 'consultor'}>
                  {userData?.role === 'admin' && <MenuItem value="admin">Administrador</MenuItem>}
                  <MenuItem value="consultor">Consultor</MenuItem>
                  <MenuItem value="colaborador">Colaborador</MenuItem>
                </TextField>
              </Grid>
              {formData.role === 'colaborador' && userData?.role === 'admin' && (
                <Grid item xs={12}>
                  <TextField required select fullWidth name="consultorId" label="Distribuidor (Admin/Consultor)" value={formData.consultorId} onChange={handleChange}>
                    {users.filter(u => u.role === 'admin' || u.role === 'consultor').map(u => (
                      <MenuItem key={u._id} value={u._id}>{u.name} ({u.role})</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              {(formData.role === 'consultor' || formData.role === 'admin') && (
                <Grid item xs={12}><TextField required fullWidth name="immunocalUrl" label="URL Tienda Immunocal" value={formData.immunocalUrl} onChange={handleChange} /></Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose}>Cancelar</Button>
          <Button onClick={handleCreateSubmit} variant="contained" disabled={loading}>Crear Usuario</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo Editar */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        {error && <Box sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>{error}</Box>}
        <DialogTitle>Editar Usuario</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField required fullWidth name="email" label="Email" value={formData.email} onChange={handleChange} disabled /></Grid>
              <Grid item xs={12}><TextField required fullWidth name="name" label="Nombre" value={formData.name} onChange={handleChange} /></Grid>
              <Grid item xs={12}><TextField fullWidth name="phone" label="Teléfono" value={formData.phone} onChange={handleChange} /></Grid>
              <Grid item xs={12}><TextField required fullWidth name="url" label="URL Personalizada" value={formData.url} onChange={handleChange} helperText="Esta URL será parte de su enlace público" /></Grid>
              <Grid item xs={12}>
                <TextField required select fullWidth name="role" label="Rol" value={formData.role} onChange={handleChange}
                  disabled={userData?.role !== 'admin'}>
                  {userData?.role === 'admin' && <MenuItem value="admin">Administrador</MenuItem>}
                  <MenuItem value="consultor">Consultor</MenuItem>
                  <MenuItem value="colaborador">Colaborador</MenuItem>
                </TextField>
              </Grid>
              {formData.role === 'colaborador' && (
                <Grid item xs={12}>
                  <TextField required select fullWidth name="consultorId" label="Distribuidor (Admin/Consultor)" value={formData.consultorId} onChange={handleChange}>
                    {users.filter(u => u.role === 'admin' || u.role === 'consultor').map(u => (
                      <MenuItem key={u._id} value={u._id}>{u.name} ({u.role})</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              {(formData.role === 'consultor' || formData.role === 'admin') && (
                <Grid item xs={12}><TextField required fullWidth name="immunocalUrl" label="URL Tienda Immunocal" value={formData.immunocalUrl} onChange={handleChange} /></Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancelar</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={loading}>Guardar Cambios</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo Cambiar Contraseña */}
      <Dialog open={passwordDialogOpen} onClose={handlePasswordClose} maxWidth="sm" fullWidth>
        {error && <Box sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>{error}</Box>}
        <DialogTitle>Cambiar Contraseña</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>Cambiar contraseña para <strong>{selectedUser?.name}</strong></Typography>
            <TextField fullWidth margin="normal" label="Nueva Contraseña" type="password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} autoFocus required
              helperText="La contraseña debe tener al menos 6 caracteres" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordClose}>Cancelar</Button>
          <Button onClick={handlePasswordSubmit} variant="contained" color="primary"
            disabled={loading || !newPassword || newPassword.length < 6}>Cambiar Contraseña</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo Eliminar */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose} maxWidth="sm" fullWidth>
        {error && <Box sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>{error}</Box>}
        <DialogTitle>Eliminar Usuario</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">¿Estás seguro de que deseas eliminar al usuario <strong>{selectedUser?.name}</strong>?</Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>Esta acción no se puede deshacer. El usuario será eliminado permanentemente.</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancelar</Button>
          <Button onClick={handleDeleteSubmit} variant="contained" color="error" disabled={loading}>Eliminar Usuario</Button>
        </DialogActions>
      </Dialog>

      {/* Notificación */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Diálogo QR */}
      <Dialog open={qrDialogOpen} onClose={handleQrClose} maxWidth="sm" fullWidth>
        <DialogTitle>Código QR de {selectedQrUser?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
            {selectedQrUser && (
              <>
                <Box id="qr-code-container">
                  <QRCodeSVG value={`https://immunoworld.net/${selectedQrUser.url}`} size={300} id="qr-code-svg" />
                </Box>
                <Typography variant="body1" align="center">Este código QR dirige a la página pública:</Typography>
                <Typography variant="body2" color="primary" sx={{ wordBreak: 'break-all' }}>
                  https://immunoworld.net/{selectedQrUser.url}
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {selectedQrUser && (
            <Button startIcon={<DownloadIcon />} onClick={() => {
              const svgElement = document.getElementById('qr-code-svg');
              if (!svgElement) return;
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              if (!ctx) return;
              canvas.width = 300; canvas.height = 300;
              const svgData = new XMLSerializer().serializeToString(svgElement);
              const img = new Image();
              img.onload = () => {
                ctx.fillStyle = 'white'; ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0);
                const jpgUrl = canvas.toDataURL('image/jpeg', 1.0);
                const link = document.createElement('a');
                link.href = jpgUrl; link.download = `qr-${selectedQrUser.url}.jpg`;
                document.body.appendChild(link); link.click(); document.body.removeChild(link);
              };
              img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
            }} variant="outlined" color="primary">Descargar JPG</Button>
          )}
          <Button onClick={handleQrClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
